import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Link
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LogTheme from './LogTheme';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../utils/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';

// Samma innehåll som sidan /pricing
const includedInBoth = [
  'Konto och säker inloggning',
  'Din egen loggbok',
  'Möjlighet att redigera och ta bort egna loggar',
];
const basFeatures = [
  '10 tripploggar',
  '10 serviceloggar',
  'Kostnadsfritt – följer automatiskt med när du skapar konto',
];
const premiumFeatures = [
  'Obegränsat antal tripploggar',
  'Obegränsat antal serviceloggar',
  'Bjud in andra användare till loggboken – de kan läsa och bidra med innehåll',
];

const Subscription = () => {
  const { userId, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [selectedPlanForInterval, setSelectedPlanForInterval] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('monthly');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasSessionId = params.get('session_id');
    if (!currentUser) {
      // Skicka inte bort användaren när de kommer tillbaka från Stripe – vänta tills auth har laddats
      if (hasSessionId) return;
      navigate('/info', { replace: true });
      return;
    }
    fetchPlans();
    fetchSubscriptionStatus();
  }, [userId, currentUser, navigate, location.search]);

  // Om vi har session_id men användaren inte är inloggad efter att auth laddat – skicka till info
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('session_id')) return;
    if (currentUser) return;
    const t = setTimeout(() => {
      navigate('/info', { replace: true });
    }, 2500);
    return () => clearTimeout(t);
  }, [location.search, currentUser, navigate]);

  // Efter retur från Stripe: verifiera session_id, uppdatera status, rensa URL och visa tack
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId || !currentUser) return;

    const verifyAndRedirect = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscription/verify-session`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ session_id: sessionId })
        });
        if (response.ok) {
          const data = await response.json();
          setCurrentSubscription(data);
          setShowSuccessMessage(true);
        }
      } catch (err) {
        console.error('Error verifying session:', err);
      }
      navigate('/subscription', { replace: true });
    };
    verifyAndRedirect();
  }, [location.search, currentUser, navigate]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscription/plans`);
      if (response.ok) {
        const data = await response.json();
        // Visa bara Bas och Premium – filtrera bort Max om API skulle returnera det
        setPlans(data.filter((p) => p.id !== 'max'));
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscription/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentSubscription(data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId, interval) => {
    if (planId === 'basic') {
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ planId: 'premium', interval: interval || 'monthly' })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          alert('Fel vid skapande av checkout session.');
        }
      } else {
        const err = await response.json().catch(() => ({}));
        const msg = err.errorDetail
          ? `${err.message || 'Fel vid skapande av checkout session.'}\n\nDetalj: ${err.errorDetail}`
          : (err.message || 'Fel vid skapande av checkout session.');
        alert(msg);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Något gick fel.');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscription/portal`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          alert('Kunde inte öppna betalningsinställningar.');
        }
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.message || 'Kunde inte öppna betalningsinställningar.');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      alert('Något gick fel.');
    }
  };

  const getPlanName = (planId) => {
    if (planId === 'max') return 'Premium'; // Max används inte längre, visa som Premium
    const plan = plans.find(p => p.id === planId);
    return plan ? plan.name : planId;
  };

  const renderFeatureList = (items) => (
    <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
      {items.map((item, i) => (
        <Box key={i} component="li" display="flex" alignItems="flex-start" sx={{ mb: 1 }}>
          <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1, mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2">{item}</Typography>
        </Box>
      ))}
    </Box>
  );

  const isCancel = location.pathname === '/subscription/cancel';

  const basicPlan = plans.find((p) => p.id === 'basic');

  return (
    <LogTheme>
      <Box padding={{ xs: 2, md: 4 }} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Typography variant="h1" gutterBottom>
          Prenumerationsplaner
        </Typography>

        {showSuccessMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowSuccessMessage(false)}>
            Tack! Din Premium-prenumeration är nu aktiverad.
          </Alert>
        )}
        {isCancel && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Du avbröt betalningen. Du kan när som helst välja Premium igen nedan.
          </Alert>
        )}

        {currentSubscription && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h2" gutterBottom>
                Nuvarande prenumeration
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                <Chip
                  label={getPlanName(currentSubscription.plan)}
                  color="primary"
                  size="large"
                />
                {currentSubscription.plan === 'premium' && currentSubscription.interval && (
                  <Chip
                    label={currentSubscription.interval === 'yearly' ? '390 kr/år' : '39 kr/månad'}
                    variant="outlined"
                    size="large"
                  />
                )}
                <Chip
                  label={currentSubscription.status === 'active' ? 'Aktiv' : (currentSubscription.status || '—')}
                  color={currentSubscription.status === 'active' ? 'success' : 'default'}
                />
              </Box>
              {currentSubscription.isGrantedByAdmin && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Din Premium är tilldelad gratis av en administratör och du betalar inget för denna plan.
                </Typography>
              )}
              {currentSubscription.cancelAtPeriodEnd && currentSubscription.endsAt && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Din prenumeration är uppsagd och gäller till och med{' '}
                  {new Date(currentSubscription.endsAt).toLocaleDateString('sv-SE', { dateStyle: 'long' })}.
                  Efter det har du tillgång till Bas-planen. Vill du ångra avslutningen kan du göra det via knappen nedan (betalningsinställningar).
                </Alert>
              )}
              {currentSubscription.endsAt && (
                <Typography variant="body2" color="text.secondary">
                  Förfaller: {new Date(currentSubscription.endsAt).toLocaleDateString('sv-SE')}
                </Typography>
              )}
              {currentSubscription.plan !== 'basic' &&
                !currentSubscription.isGrantedByAdmin &&
                currentSubscription.status !== 'canceled' && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenPortal}
                    sx={{ mt: 2 }}
                  >
                    Hantera din prenumeration
                  </Button>
                )}
            </CardContent>
          </Card>
        )}

        {selectedPlanForInterval === 'premium' && (
          <Card sx={{ mb: 4, boxShadow: 2 }}>
            <CardContent>
              <Link
                component="button"
                variant="body2"
                onClick={() => setSelectedPlanForInterval(null)}
                sx={{ mb: 2, display: 'inline-block', cursor: 'pointer' }}
              >
                ← Tillbaka
              </Link>
              <Typography variant="h2" gutterBottom>
                Välj betalningsintervall
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Välj om du vill betala månadsvis eller årsvis
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 2, display: 'block' }}>
                <RadioGroup
                  value={selectedInterval}
                  onChange={(e) => setSelectedInterval(e.target.value)}
                >
                  <FormControlLabel
                    value="monthly"
                    control={<Radio color="primary" />}
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>Månadsvis</Typography>
                        <Typography color="text.secondary">39 kr/månad</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="yearly"
                    control={<Radio color="primary" />}
                    label={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography>Årsvis</Typography>
                        <Typography color="text.secondary">390 kr/år (2 månader gratis)</Typography>
                        <Chip label="Spara 78 kr" color="success" size="small" />
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<CreditCardIcon />}
                onClick={() => handleSubscribe('premium', selectedInterval)}
                disabled={processing || currentSubscription?.isGrantedByAdmin}
                sx={{ mt: 2 }}
              >
                Fortsätt till betalning
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Du kommer att omdirigeras till Stripe för säker betalning. Klarna är tillgängligt som betalningsmetod.
              </Typography>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={3}>
          {/* Bas-planen */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                ...(currentSubscription?.plan === 'basic' && {
                  border: 2,
                  borderColor: 'primary.main'
                })
              }}
            >
              {currentSubscription?.plan === 'basic' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <CheckCircleIcon color="primary" />
                  <Typography variant="body2" color="primary">
                    Aktiv
                  </Typography>
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h3" gutterBottom>
                  {basicPlan?.name || 'Bas'}
                </Typography>
                <Typography variant="h4" gutterBottom>
                  {basicPlan?.priceLabel || 'Gratis'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Det som ingår i båda planerna:
                </Typography>
                {renderFeatureList(includedInBoth)}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  I basplanen ingår dessutom:
                </Typography>
                {renderFeatureList(basFeatures)}
                <Button
                  variant={currentSubscription?.plan === 'basic' ? 'outlined' : 'contained'}
                  fullWidth
                  sx={{ mt: 3 }}
                  disabled
                >
                  {currentSubscription?.plan === 'basic' ? 'Vald plan' : 'Gratis'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Premium-planen – en knapp som öppnar val av betalningsintervall */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                ...(currentSubscription?.plan === 'premium' && {
                  border: 2,
                  borderColor: 'primary.main'
                })
              }}
            >
              {currentSubscription?.plan === 'premium' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <CheckCircleIcon color="primary" />
                  <Typography variant="body2" color="primary">
                    Aktiv
                  </Typography>
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h3" gutterBottom>
                  Premium
                </Typography>
                <Typography variant="h4" gutterBottom>
                  {currentSubscription?.isGrantedByAdmin ? 'Gratis (tilldelad)' : '39 kr/månad'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Det som ingår i båda planerna:
                </Typography>
                {renderFeatureList(includedInBoth)}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  I Premium ingår dessutom:
                </Typography>
                {renderFeatureList(premiumFeatures)}

                <Button
                  variant={
                    currentSubscription?.plan === 'premium' ? 'outlined' : 'contained'
                  }
                  fullWidth
                  startIcon={currentSubscription?.plan !== 'premium' ? <CreditCardIcon /> : null}
                  sx={{ mt: 3 }}
                  onClick={() =>
                    currentSubscription?.plan === 'premium'
                      ? undefined
                      : setSelectedPlanForInterval('premium')
                  }
                  disabled={
                    currentSubscription?.plan === 'premium' ||
                    currentSubscription?.isGrantedByAdmin
                  }
                >
                  {currentSubscription?.plan === 'premium'
                    ? 'Nuvarande plan'
                    : 'Välj Premium'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </LogTheme>
  );
};

export default Subscription;



